<template>
  <Offcanvas name="dataProviderEditOffcanvas">
    <template v-slot:header>
      <h4>{{ $t('data_providers.form.edit_title') }}</h4>
    </template>
    <template v-slot:default>
      <form @submit.prevent="update" v-if="editingDataProvider">
        <DataProviderForm v-model="editingDataProvider" :is-edit="true"/>
        <div class="mt-3">
          <button class="btn btn-primary w-100">{{ $t('data_providers.form.update_button') }}</button>
        </div>
      </form>
    </template>
    <template v-slot:footer>
      <div class="text-end" v-if="editingDataProvider">
        <button class="btn btn-danger" @click="del">{{ $t('data_providers.delete') }}</button>
      </div>
    </template>
  </Offcanvas>
</template>

<script setup>
import {ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import {deleteDataProvider, updateDataProvider} from '../../../api/admin/DataProviderApi';
import Offcanvas from '../../../components/Offcanvas.vue';
import useFlashMessage from '../../../composables/useFlashMessage';
import useOffcanvas from '../../../composables/useOffcanvas';
import DataProvider from '../../../models/DataProvider';
import DataProviderForm from './DataProviderForm.vue';

const props = defineProps({
  dataProvider: DataProvider
});

const emit = defineEmits(['update']);

const {t} = useI18n();
const router = useRouter();
const {successMessage, warningMessage, errorMessage} = useFlashMessage();
const {closeOffcanvas} = useOffcanvas('dataProviderEditOffcanvas');

const editingDataProvider = ref();

watch(props, () => editingDataProvider.value = new DataProvider({...props.dataProvider}));

/**
 * Update data provider
 *
 * @returns {Promise<void>}
 */
const update = async () => {
  try {
    await updateDataProvider(editingDataProvider.value);
    successMessage(t('data_providers.messages.updated', { name: editingDataProvider.value.name }));
    emit('update');
    closeOffcanvas();
  } catch (e) {
    errorMessage(e.message);
  }
};

/**
 * Delete data provider
 *
 * @returns {Promise<void>}
 */
const del = async () => {
  if (confirm(t('data_providers.messages.confirm_delete'))) {
    try {
      await deleteDataProvider(editingDataProvider.value);
      warningMessage(t('data_providers.messages.deleted', { name: editingDataProvider.value.name }));
      closeOffcanvas();
      await router.push({name: 'admin.data-providers.index'});
    } catch (e) {
      if (e.status === 403) {
        // Do nothing
      } else {
        errorMessage(e.message);
      }
    }
  }
};
</script>